import React, { useContext, useMemo } from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from 'chart.js'
import dayjs from 'dayjs'
import { ThemeContext } from '../../context/ThemeProvider'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
)
ChartJS.defaults.font.family = 'arial'

const options = (isDark?: boolean) => ({
  responsive: true,
  elements: {
    line: {
      tension: 0.4,
    },
    point: {
      radius: 2,
    },
  },
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top' as const,
      labels: {
        color: isDark ? '#E0E0E0' : 'black',
        textShadow: isDark ? '0 0 5px rgba(255,255,255,0.3)' : 'none',
      },
    },
    tooltip: {
      mode: 'index' as const,
      intersect: false,
    },
  },
  scales: {
    x: {
      ticks: {
        color: isDark ? '#E0E0E0' : 'black',
        font: {
          weight: 'bold' as const,
        },
      },
      grid: {
        color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
      },
    },
    y: {
      beginAtZero: true,
      ticks: {
        color: isDark ? '#E0E0E0' : 'black',
        font: {
          weight: 'bold' as const,
        },
      },
      grid: {
        color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
      },
    },
  },
})

const lineColors = {
  midnight: {
    line: 'rgb(100, 100, 200)',
    fill: 'rgba(100, 100, 200, 0.2)',
  },
  'crimson-sunset': {
    line: 'rgb(255, 80, 80)',
    fill: 'rgba(255, 80, 80, 0.2)',
  },
  'emerald-glow': {
    line: 'rgb(50, 200, 50)',
    fill: 'rgba(50, 200, 50, 0.2)',
  },
  'cyber-cyan': {
    line: 'rgb(50, 200, 200)',
    fill: 'rgba(50, 200, 200, 0.2)',
  },
  'indigo-dream': {
    line: 'rgb(130, 50, 220)',
    fill: 'rgba(130, 50, 220, 0.2)',
  },
}

interface DatasetConfig {
  yKey: string
  label?: string
  colorScheme?: keyof typeof lineColors
}

interface MultiLineChartProps {
  data: Array<any>
  datasets: DatasetConfig[]
  isLoading?: boolean
  isPreviousData?: boolean
  customOptions?: Record<string, unknown>
  xKey?: string
}

const MultiLineChart = ({
  data,
  datasets,
  isLoading = false,
  isPreviousData = false,
  customOptions,
  xKey,
}: MultiLineChartProps) => {
  const { theme } = useContext(ThemeContext)
  const isDark = theme === 'dark'
  const labels = useMemo(() => {
    if (xKey) return Array.from(new Set(data.map((d) => d[xKey])))
    else return Array.from(new Set(data.map((v) => dayjs(v.date).format("MMM D, 'YY"))))
  }, [xKey, data])

  const chartData = {
    labels,
    datasets: datasets.map((dataset, idx) => {
      const key = Object.keys(lineColors)[idx % Object.keys(lineColors).length]

      const colorScheme = dataset.colorScheme ? dataset.colorScheme : key
      return {
        label: dataset.label || dataset.yKey,
        data: labels.map((l) => {
          const entry = data.find((d) =>
            xKey ? d[xKey] === l : dayjs(d.date).format("MMM D, 'YY") === l,
          ) ?? {
            [dataset.yKey]: 0,
          }
          return entry[dataset.yKey]
        }),
        borderColor: lineColors[colorScheme as keyof typeof lineColors].line,
        backgroundColor: lineColors[colorScheme as keyof typeof lineColors].fill,
        fill: true,
        borderWidth: 2,
        pointBackgroundColor: lineColors[colorScheme as keyof typeof lineColors].line,
        pointBorderColor: lineColors[colorScheme as keyof typeof lineColors].line,
        datalabels: {
          display: false,
        },
      }
    }),
  }

  return (
    <div className="relative h-full w-full">
      {isPreviousData && (
        <div className="absolute top-0 left-0 h-full w-full bg-white opacity-50" />
      )}
      <Line options={{ ...options(isDark), ...customOptions }} data={chartData} />
    </div>
  )
}

export default MultiLineChart
